import { useId, useState } from "react";
import { Box, Button, Menu, MenuItem } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { exportToCsv, exportToPrint } from "../utils/exportUtils";
import { parseImportFile } from "../utils/csvImport";
import { useLookups, toColorMap } from "../hooks/useLookups";
import { useAuth } from "../hooks/useAuth";
import { useCloseOnNavigate } from "../hooks/useCloseOnNavigate";
import ImportErrorsDialog from "./ImportErrorsDialog";
import ImportPreviewDialog from "./ImportPreviewDialog";
import ImportResultAlert from "./ImportResultAlert";

export default function ExportMenu({
  initiatives,
  onImport,
  canExport,
  canImport,
}) {
  const [anchor, setAnchor] = useState(null);
  const [importErrors, setImportErrors] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importInputId = useId();
  const lookups = useLookups();
  const { user } = useAuth();

  useCloseOnNavigate(() => {
    setImportPreview(null);
    setImportErrors(null);
  });

  const colorMaps = {
    horizon: toColorMap(lookups.horizons),
    status: toColorMap(lookups.statuses),
    priority: toColorMap(lookups.priorities),
  };

  const styles = {
    btn: {
      color: "background.tableHeaderText",
      fontWeight: 500,
      fontSize: "0.8125rem",
      textTransform: "none",
    },
    icon: {
      transition: "transform 0.15s",
      transform: anchor ? "rotate(180deg)" : "none",
    },
    fileInput: { display: "none" },
  };

  const handleOpen = (e) => setAnchor(e.currentTarget);
  const handleClose = () => setAnchor(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;

    setImportErrors(null);
    setImportResult(null);

    const text = await file.text();
    const { items, errors } = parseImportFile(text, lookups);
    if (errors.length > 0) {
      setImportErrors(errors);
      return;
    }
    setImportPreview(items);
  };

  const handleConfirmImport = async (selectedItems) => {
    setImporting(true);
    const { created, failed } = await onImport(selectedItems);
    setImporting(false);
    setImportPreview(null);
    setImportResult({ createdCount: created.length, failed });
  };

  const handleCancelImport = () => setImportPreview(null);

  if (!canExport && !canImport) return null;

  return (
    <>
      <Button
        size="small"
        sx={styles.btn}
        onClick={handleOpen}
        endIcon={<ExpandMore fontSize="small" sx={styles.icon} />}
      >
        Import / Export
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={handleClose}>
        {canExport && (
          <MenuItem
            dense
            onClick={() => {
              exportToCsv(initiatives);
              handleClose();
            }}
          >
            Download CSV
          </MenuItem>
        )}
        {canExport && (
          <MenuItem
            dense
            disabled={lookups.loading}
            onClick={() => {
              exportToPrint(
                initiatives,
                colorMaps,
                lookups.horizons.map((h) => h.name),
                user?.name ?? null,
              );
              handleClose();
            }}
          >
            Save as PDF
          </MenuItem>
        )}
        {canImport && (
          <MenuItem
            dense
            component="label"
            htmlFor={importInputId}
            disabled={lookups.loading}
            onClick={handleClose}
          >
            Import from CSV
          </MenuItem>
        )}
      </Menu>
      <Box
        component="input"
        id={importInputId}
        type="file"
        accept=".csv,text/csv"
        sx={styles.fileInput}
        onChange={handleFileChange}
      />
      <ImportErrorsDialog
        open={Boolean(importErrors)}
        errors={importErrors || []}
        onClose={() => setImportErrors(null)}
      />
      <ImportPreviewDialog
        open={Boolean(importPreview)}
        items={importPreview || []}
        onConfirm={handleConfirmImport}
        onCancel={handleCancelImport}
        importing={importing}
      />
      <ImportResultAlert
        result={importResult}
        onClose={() => setImportResult(null)}
      />
    </>
  );
}
